import { Directive, ElementRef, PLATFORM_ID, afterNextRender, effect, inject, input, InputSignal, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// Allowed split modes; keep open for custom plugins
type SplitMode = 'chars' | 'words' | 'lines' | 'items' | 'cells' | (string & {});

type SplittingOptions = {
  /**
   * The target element, either a string selector or element(s)
   * @default '[data-splitting]'
   */
  target?: string | Element | Element[] | NodeList | undefined;
  /**
   * The name of the plugin to use
   * @default 'chars'
   */
  by?: string | undefined;
  /**
   * An optional string to prefix the CSS variables with
   * @default null
   */
  key?: string | null | undefined;

  /** Any plugin-specific options */
  [key: string]: unknown;
};

@Directive({
  selector: '[splitting]',
  standalone: true,
})
export class SplittingDirective {
  private host = inject(ElementRef<HTMLElement>);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  // --- signal inputs (Angular 17+ API) ---
  /** What to split: 'chars' | 'words' | 'lines' */
  by: InputSignal<SplitMode> = input<SplitMode>('chars');

  /** 'preserve' keeps spaces as separate nodes; 'collapse' matches normal layout */
  whitespace: InputSignal<'preserve' | 'collapse'> = input<'preserve' | 'collapse'>('collapse');

  /** Forward any Splitting.js options (partial) */
  options: InputSignal<Partial<SplittingOptions>> = input<Partial<SplittingOptions>>({});

  /** Watch element text changes and re-run splitting */
  observe: InputSignal<boolean> = input<boolean>(false);

  /** Restore original HTML before re-splitting (prevents nested spans) */
  rerenderOnChange: InputSignal<boolean> = input<boolean>(true);

  // --- internal state ---
  private originalHTML: string | null = null;
  private ran = false;
  private observer?: MutationObserver;

  constructor() {
    const el = this.host.nativeElement;
    if (!this.isBrowser) {
      el.style.display = 'none';
      return;
    }

    el.style.display = 'initial';

    // Run once after the next paint for maximum DOM stability
    afterNextRender(() => {
      this.captureOriginal();
      this.applySplitting();
      if (this.observe()) this.startObserver();
    });

    // Reactively re-run when any relevant input changes
    effect(() => {
      if (!this.isBrowser) return;
      if (!this.ran) return; // ignore initial effect until first run

      const mode = this.by();
      const ws = this.whitespace();
      const opts = this.options();
      const shouldObserve = this.observe();
      const shouldRestore = this.rerenderOnChange();
      // Accessing signals above wires them to this effect

      // toggle observer
      if (shouldObserve) this.startObserver();
      else this.stopObserver();

      // re-run splitting
      if (shouldRestore && this.originalHTML !== null) {
        this.host.nativeElement.innerHTML = this.originalHTML;
      }
      queueMicrotask(() => this.applySplitting({ mode, ws, opts }));
    });
  }

  // ---- helpers ----

  private captureOriginal() {
    if (this.originalHTML === null) {
      this.originalHTML = this.host.nativeElement.innerHTML;
    }
  }

  private applySplitting(input?: { mode?: SplitMode; ws?: 'preserve' | 'collapse'; opts?: Partial<SplittingOptions> }) {
    const el = this.host.nativeElement;

    el.setAttribute('data-splitting', '');

    (<any>window).Splitting({
      target: el,
      by: input?.mode ?? this.by(),
      whitespace: input?.ws ?? this.whitespace(),
      ...(input?.opts ?? this.options()),
    });

    // Special handling: if last character is a period, remove it from the last word span
    const lastWordEl = el.querySelector('.word:last-child');
    const lastWordAttr = lastWordEl?.getAttribute('data-word');

    if (lastWordAttr && lastWordAttr.endsWith('.')) {
      lastWordEl.setAttribute('data-word', lastWordAttr.slice(0, -1));
    }

    this.ran = true;
  }

  private startObserver() {
    if (this.observer) return;
    if (!this.isBrowser) return;

    this.observer = new MutationObserver(() => {
      // If content changed, re-split (restore first to avoid nesting)
      if (this.originalHTML !== null) {
        this.host.nativeElement.innerHTML = this.originalHTML;
        // Re-capture in case true content changed
        this.originalHTML = this.host.nativeElement.innerHTML;
      }
      this.applySplitting();
    });

    this.observer.observe(this.host.nativeElement, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  private stopObserver() {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
